export type SameSiteOption = 'Strict' | 'Lax' | 'None';

export interface CookieOptions {
  path?: string;
  domain?: string;
  maxAge?: number;
  expires?: Date;
  sameSite?: SameSiteOption;
  secure?: boolean;
}

const isBrowser = typeof document !== 'undefined';

export const COOKIE_KEYS = {
  SIDEBAR_STATE: 'sidebar:state',
  CHANGELOG_LAST_SEEN: 'last_seen_version',
  CHANGELOG_SUPPRESS: 'changelog:suppress',
  CHANGELOG_HAS_LOGGED_IN: 'changelog:has_logged_in',
} as const;

type CookieRecord = Record<string, string>;

export function parseCookies(): CookieRecord {
  if (!isBrowser || !document.cookie) {
    return {};
  }

  return document.cookie.split('; ').reduce<CookieRecord>((acc, cookie) => {
    const [rawName, ...rawValue] = cookie.split('=');
    if (!rawName) {
      return acc;
    }

    const name = decodeURIComponent(rawName);
    const value = decodeURIComponent(rawValue.join('='));
    acc[name] = value;
    return acc;
  }, {});
}

export function getCookie(name: string): string | undefined {
  if (!isBrowser) {
    return undefined;
  }
  const cookies = parseCookies();
  return cookies[name];
}

function shouldUseSecureFlag(secure?: boolean): boolean {
  if (typeof secure === 'boolean') {
    return secure;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.protocol === 'https:';
}

export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (!isBrowser) {
    return;
  }

  const {
    path = '/',
    domain,
    maxAge,
    expires,
    sameSite = 'Lax',
  } = options;
  const secure = shouldUseSecureFlag(options.secure ?? (sameSite === 'None' ? true : undefined));

  const segments: string[] = [];
  segments.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);

  if (typeof maxAge === 'number') {
    segments.push(`Max-Age=${Math.floor(maxAge)}`);
  }

  if (expires instanceof Date) {
    segments.push(`Expires=${expires.toUTCString()}`);
  }

  if (path) {
    segments.push(`Path=${path}`);
  }

  if (domain) {
    segments.push(`Domain=${domain}`);
  }

  if (sameSite) {
    segments.push(`SameSite=${sameSite}`);
  }

  if (secure) {
    segments.push('Secure');
  }

  document.cookie = segments.join('; ');
}

export function deleteCookie(name: string, options: Omit<CookieOptions, 'maxAge' | 'expires'> = {}): void {
  setCookie(name, '', { ...options, maxAge: 0 });
}
